import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { ConfigService, CommonConfig } from "../../services/config-service";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";

interface ICommonConfigInput {
  configKey?: string;
  settingsGroupName?: string;
  config?: CommonConfig;
  configId?: string;
}

interface IConfigSuccessResult {
  success: true;
  configKey: string;
  configId?: string;
  settings?: unknown[];
}

async function refreshDesignerConfigs(sd: IScreenDesignContextState): Promise<void> {
  if (typeof sd?.fetchConfigs !== "function") {
    return;
  }

  await new Promise<void>((resolve) => {
    sd.fetchConfigs({
      shouldLockPage: true,
      shouldFetchGlobals: true,
      callback: () => resolve()
    });
  });
}

export function listDesignerCommonConfigSettings(
  sdInstance: ScreenDesignInstance,
  input: ICommonConfigInput
): IConfigSuccessResult | { error: string } {
  const sd = getValues(sdInstance);
  const configKey = input?.configKey || input?.settingsGroupName;
  if (!configKey) {
    return { error: "configKey is required" };
  }

  const settings = sd.commonSettings as unknown as Record<string, unknown[]>;

  return {
    success: true,
    configKey,
    settings: settings?.[configKey] || []
  };
}

export async function createDesignerCommonConfig(
  sdInstance: ScreenDesignInstance,
  input: ICommonConfigInput
): Promise<IConfigSuccessResult | { error: string }> {
  const sd = getValues(sdInstance);
  const configKey = input?.configKey || input?.settingsGroupName;
  const config = input?.config;

  if (!sd?.project?.id) {
    return { error: "Active project is required" };
  }
  if (!configKey) {
    return { error: "configKey is required" };
  }
  if (!config) {
    return { error: "config is required" };
  }

  const response = await ConfigService.createCommonConfig(sd.project.id, configKey, config);
  await refreshDesignerConfigs(sd);

  return {
    success: true,
    configKey,
    configId: response?.data?.id || config.id
  };
}

export async function updateDesignerCommonConfig(
  sdInstance: ScreenDesignInstance,
  input: ICommonConfigInput
): Promise<IConfigSuccessResult | { error: string }> {
  const sd = getValues(sdInstance);
  const configKey = input?.configKey || input?.settingsGroupName;
  const config = input?.config;

  if (!sd?.project?.id) {
    return { error: "Active project is required" };
  }
  if (!configKey) {
    return { error: "configKey is required" };
  }
  if (!config) {
    return { error: "config is required" };
  }

  const response = await ConfigService.updateCommonConfig(sd.project.id, configKey, config);
  await refreshDesignerConfigs(sd);

  return {
    success: true,
    configKey,
    configId: response?.data?.id || config.id
  };
}

export async function deleteDesignerCommonConfig(
  sdInstance: ScreenDesignInstance,
  input: ICommonConfigInput
): Promise<IConfigSuccessResult | { error: string }> {
  const sd = getValues(sdInstance);
  const configKey = input?.configKey || input?.settingsGroupName;
  const configId = input?.configId;

  if (!sd?.project?.id) {
    return { error: "Active project is required" };
  }
  if (!configKey) {
    return { error: "configKey is required" };
  }
  if (!configId) {
    return { error: "configId is required" };
  }

  await ConfigService.deleteCommonConfig(sd.project.id, configKey, configId);
  await refreshDesignerConfigs(sd);

  return {
    success: true,
    configKey,
    configId
  };
}
